import trySaveMessage from '../../actions/trySaveMessage';
import ComposeViewState from '../../store/schema/ComposeViewState';
import createSignatureBlock from '../../utils/createSignatureBlock';
import getSignature from '../../utils/getSignature';
import { isViewStateDirty } from '../../utils/viewStateUtils';
import { AppendOnSend, CoercionType } from 'owa-addins-core';
import updateContent from 'owa-editor/lib/actions/updateContent';
import BodyType from 'owa-service/lib/contract/BodyType';
import { Editor, EditorPlugin } from 'roosterjs-editor-core';
import {
    ChangeSource,
    ContentChangedEvent,
    ContentPosition,
    PluginEvent,
    PluginEventType,
    PositionType,
} from 'roosterjs-editor-types';
import removeAppendOnSend from 'owa-mail-compose-store/lib/actions/removeAppendOnSend';
import datapoints from '../../datapoints';
import { logUsage } from 'owa-analytics';

interface CursorSnapshot {
    snapshot?: string;
    htmlSnapshotWithCursorMarker?: string;
}

const OWA_REFERENCE_ATTACHMENT_SELECTOR = '#OwaReferenceAttachments';
const OWA_REFERENCE_ATTACHMENT_END_SELECTOR = '#OwaReferenceAttachmentsEnd';

export default class ContentOperationPlugin implements EditorPlugin {
    private editor: Editor;
    private plainTextEditor: HTMLTextAreaElement;
    private saveOnDispose: boolean;
    private cursorSnapshot: CursorSnapshot;
    private replyWithTextElementId: string;
    private hiddenAttachmentsHtml = '';

    constructor(private viewState: ComposeViewState) {}

    public getName() {
        return 'ContentOperation';
    }

    public initialize(editor: Editor) {
        this.editor = editor;
        this.saveOnDispose = true;
    }

    public dispose() {
        if (this.saveOnDispose) {
            this.cursorSnapshot = {};
            if (this.editor && this.editor.getSelectionRange()) {
                this.cursorSnapshot.htmlSnapshotWithCursorMarker = this.editor.getContent(
                    false /*triggerExtractContentEvent*/,
                    true /*includeSelectionMarker*/
                );
            }

            this.updateContentToViewState();
            this.cursorSnapshot.snapshot = this.viewState.content;

            if (
                isViewStateDirty(this.viewState) &&
                !this.viewState.subjectPickerViewState.isLoadingConversation
            ) {
                trySaveMessage(this.viewState, true /*isAutoSave*/);
            }
        }

        this.saveOnDispose = false;
        this.editor = null;
    }

    public hasCursorSnapshot() {
        return this.cursorSnapshot && this.cursorSnapshot.htmlSnapshotWithCursorMarker;
    }

    public focusToEditor() {
        if (this.editor) {
            this.editor.focus();
            if (
                this.hasCursorSnapshot() &&
                this.cursorSnapshot.snapshot == this.viewState.content
            ) {
                this.editor.setContent(
                    this.cursorSnapshot.htmlSnapshotWithCursorMarker,
                    undefined /* triggerContentChangedEvent */,
                    this.editor.isDarkMode()
                );
                this.editor.triggerContentChangedEvent('RestoreCursor');
            }
        } else if (this.plainTextEditor) {
            this.plainTextEditor.focus();
            this.plainTextEditor.selectionStart = 0;
            this.plainTextEditor.selectionEnd = 0;
        }
        this.cursorSnapshot = null;
    }

    public stopSaveOnDispose() {
        this.saveOnDispose = false;
    }

    public updateContentToViewState() {
        const content = this.editor
            ? this.editor.getContent(true /*triggerExtractContentEvent*/)
            : this.plainTextEditor
            ? this.plainTextEditor.value
            : null;

        if (content != null) {
            updateContent(this.viewState, content);
        }
    }

    public setPlainTextEditor(plainTextEditor: HTMLTextAreaElement) {
        this.plainTextEditor = plainTextEditor;
    }

    public onPluginEvent(event: PluginEvent): void {
        if (event.eventType === PluginEventType.EditorReady) {
            // If the editor body contains hidden attachment helper nodes, temporarily remove them,
            // and we will add them back when retrieve the HTML content.
            const divOwaReferenceAttachments = this.editor.queryElements(
                OWA_REFERENCE_ATTACHMENT_SELECTOR
            )[0];
            const divOwaReferenceAttachmentsEnd = this.editor.queryElements(
                OWA_REFERENCE_ATTACHMENT_END_SELECTOR
            )[0];

            if (
                divOwaReferenceAttachments &&
                !divOwaReferenceAttachments.previousElementSibling &&
                divOwaReferenceAttachmentsEnd &&
                divOwaReferenceAttachmentsEnd.previousElementSibling == divOwaReferenceAttachments
            ) {
                this.hiddenAttachmentsHtml =
                    (divOwaReferenceAttachments ? divOwaReferenceAttachments.outerHTML : '') +
                    (divOwaReferenceAttachmentsEnd ? divOwaReferenceAttachmentsEnd.outerHTML : '');
                this.editor.deleteNode(divOwaReferenceAttachments);
                this.editor.deleteNode(divOwaReferenceAttachmentsEnd);
            }
        } else if (event.eventType === PluginEventType.ExtractContent) {
            // Add the attachment helper nodes back
            event.content = this.hiddenAttachmentsHtml + event.content;
        } else if (event.eventType === PluginEventType.ContentChanged) {
            const contentChangedEvent: ContentChangedEvent = event as ContentChangedEvent;

            if (contentChangedEvent.source === ChangeSource.SetContent) {
                if (this.replyWithTextElementId) {
                    // Wait until the text element has been inserted before moving the caret
                    this.moveCaretBelowHtmlElement(this.replyWithTextElementId);
                }
            }
        }
    }

    public insertSignature(bodyType: BodyType) {
        const signatureText = createSignatureBlock(bodyType, getSignature(bodyType));

        if (bodyType == 'HTML' && this.editor) {
            this.editor.focus();
            this.editor.addUndoSnapshot();
            this.editor.insertContent(signatureText, {
                position: ContentPosition.SelectionStart,
                updateCursor: false,
                replaceSelection: true,
                insertOnNewLine: true,
            });
            this.editor.triggerContentChangedEvent('Signature');
            this.editor.addUndoSnapshot();
        } else if (bodyType == 'Text' && this.plainTextEditor) {
            const cursorPosition = this.plainTextEditor.selectionEnd;
            const newContent =
                this.plainTextEditor.value.substr(0, cursorPosition) +
                signatureText +
                this.plainTextEditor.value.substr(cursorPosition);
            this.plainTextEditor.value = newContent;
        }
    }

    public insertAppendOnSend(bodyType: BodyType) {
        const appendOnSend: AppendOnSend[] = this.viewState.appendOnSend;
        let stringToAppend: string = '';
        const { name, customData } = datapoints.AppendOnSendEvent.datapoint;
        if (bodyType == 'HTML' && this.editor) {
            appendOnSend.map(value => {
                let textToAppend: string = value.txt;
                if (value.typ === CoercionType.Text) {
                    textToAppend = this.escapeAppendOnSendForHtml(textToAppend);
                }
                stringToAppend += textToAppend;
            });
            const div: HTMLElement[] = this.editor.queryElements('div#appendonsend');
            if (div.length == 0) {
                // div is not found
                logUsage(name, customData('End', 'HTML'));
                this.editor.insertContent(stringToAppend, {
                    position: ContentPosition.End,
                    updateCursor: false,
                    replaceSelection: false,
                    insertOnNewLine: true,
                });
            } else {
                for (let i = 0; i < div.length; i++) {
                    const element: HTMLElement = div[i];
                    if (element.id === 'appendonsend' && element.innerHTML === '') {
                        logUsage(name, customData('Div', 'HTML'));
                        element.innerHTML = stringToAppend;
                        break;
                    }
                }
            }
        } else if (bodyType == 'Text' && this.plainTextEditor) {
            appendOnSend.map(value => {
                if (value.typ !== CoercionType.Html) {
                    stringToAppend += '\n' + value.txt;
                }
            });
            logUsage(name, customData('End', 'Text'));
            const newContent = this.plainTextEditor.value + stringToAppend;
            this.plainTextEditor.value = newContent;
        }
        removeAppendOnSend(this.viewState);
    }

    private escapeAppendOnSendForHtml(content: string) {
        content = content.replace(/&/g, '&amp;');
        content = content.replace(/</g, '&lt;');
        content = content.replace(/>/g, '&gt;');
        content = content.replace(/"/g, '&quot;');
        content = content.replace(/'/g, '&#39;');
        content = content.replace(/\//g, '&#x2F;');
        return content;
    }

    public moveCaretBelowHtmlElement(elementId: string) {
        if (!this.editor) {
            // The first time this method is called, the editor is not yet initialized and the
            // quote element has not yet been inserted
            // Remember the element's ID for now, and this function will be called again later
            this.replyWithTextElementId = elementId;
            return;
        }

        this.placeCursorAfterElement(elementId);
    }

    public isEmpty() {
        return this.editor && this.editor.isEmpty();
    }

    public removeInlineImage() {
        this.editor.queryElements('img', image => {
            this.editor.deleteNode(image);
        });
    }

    public getTextContent() {
        return this.editor && this.editor.getTextContent();
    }

    public insertContentIntoEditor(
        content: string,
        eventName: string,
        belowExistingContent: boolean,
        setFocusToEditor: boolean
    ) {
        const isHtml = this.viewState.bodyType == 'HTML';
        if (isHtml && this.editor) {
            if (setFocusToEditor) {
                this.editor.focus();
            }

            this.editor.addUndoSnapshot();

            this.editor.insertContent(content, {
                insertOnNewLine: belowExistingContent,
                position: belowExistingContent
                    ? ContentPosition.End
                    : ContentPosition.SelectionStart,
                replaceSelection: false,
                updateCursor: !belowExistingContent,
            });

            this.editor.triggerContentChangedEvent(eventName);
            this.editor.addUndoSnapshot();
        } else if (!isHtml && this.plainTextEditor) {
            const start = this.plainTextEditor.selectionStart;
            const end = this.plainTextEditor.selectionEnd;

            if (belowExistingContent) {
                this.plainTextEditor.value += content;
                this.plainTextEditor.selectionStart = start;
                this.plainTextEditor.selectionEnd = end;
            } else {
                this.plainTextEditor.value =
                    this.plainTextEditor.value.substr(0, start) +
                    content +
                    this.plainTextEditor.value.substr(end);
            }
        }

        // We depend on ReactEdtior updating the viewState's content property onBlur.
        // Therefore, we manually set the viewState's content property here.
        // If this changes, this will need to be udpated.
        if (!setFocusToEditor || !isHtml) {
            this.updateContentToViewState();
        }
    }

    private placeCursorAfterElement(elementId: string) {
        const element = this.editor.getDocument().getElementById(elementId);
        if (element) {
            this.editor.focus();
            this.editor.select(element, PositionType.After);
        }
    }
}
